/**
 * Screen snapshot assembly (plan 1968 F13) — pure, tested.
 *
 * Publishers (KPI cards, chart widgets, the list shell, the detail registry) put parts
 * into pageContextStore.screenParts keyed by their own publisher id. Here, at capture
 * time: keep the parts published on the current path, build lazy ones, and merge parts
 * with the same section id into ONE section (e.g. every KPI card of a dashboard ->
 * one "business-dashboard.kpis" section — two cards never overwrite each other).
 * No component section on the page -> text fallback (main content innerText).
 */

import type { PublishedScreenPart } from "./pageContextStore";
import { PAGE_CONTEXT_LIMITS, type ScreenContext, type ScreenSection } from "./types";

function buildPart(part: PublishedScreenPart): ScreenSection | null {
  try {
    return typeof part.source === "function" ? part.source() : part.source;
  } catch {
    return null;
  }
}

function isEmpty(section: ScreenSection): boolean {
  return !section.kpis?.length && !section.charts?.length && !section.fields?.length && !section.table;
}

function mergeInto(target: ScreenSection, part: Omit<ScreenSection, "id">): ScreenSection {
  return {
    ...target,
    ...(target.title || !part.title ? {} : { title: part.title }),
    ...(part.kpis?.length ? { kpis: [...(target.kpis ?? []), ...part.kpis] } : {}),
    ...(part.charts?.length ? { charts: [...(target.charts ?? []), ...part.charts] } : {}),
    ...(part.fields?.length ? { fields: [...(target.fields ?? []), ...part.fields] } : {}),
    ...(target.table || !part.table ? {} : { table: part.table }),
  };
}

/** Sections on `pathname`, in first-publish order, parts merged by section id. */
export function selectScreenSections(
  parts: Record<string, PublishedScreenPart>,
  pathname: string,
): ScreenSection[] {
  const byId = new Map<string, ScreenSection>();
  const ordered = Object.values(parts)
    .filter((part) => part.path === pathname)
    .sort((a, b) => a.order - b.order);
  for (const part of ordered) {
    const built = buildPart(part);
    if (!built || isEmpty(built)) continue;
    const previous = byId.get(part.sectionId);
    const { id: _publisherSectionId, ...content } = built;
    byId.set(part.sectionId, previous ? mergeInto(previous, content) : { id: part.sectionId, ...content });
  }
  return [...byId.values()].slice(0, PAGE_CONTEXT_LIMITS.maxScreenSections);
}

export interface BuildScreenArgs {
  aiPanelOpen: boolean;
  parts: Record<string, PublishedScreenPart>;
  pathname: string;
  at: string;
  /** Text fallback reader (main content innerText, already capped) */
  readText: () => { text: string; truncated: boolean };
}

/** The `screen` key: undefined while the AI panel is closed or nothing is readable. */
export function buildScreenContext(args: BuildScreenArgs): ScreenContext | undefined {
  if (!args.aiPanelOpen) return undefined;
  const sections = selectScreenSections(args.parts, args.pathname);
  if (sections.length) return { at: args.at, source: "components", sections };
  const { text, truncated } = args.readText();
  if (!text) return undefined;
  return { at: args.at, source: "text", sections: [], text, ...(truncated ? { truncated: true } : {}) };
}
